import { useEffect, useState } from "react";
import { toast } from "sonner";
import { AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { api, errMsg } from "@/lib/api";
import { fmtMoney, fmtPrice, ORDER_TYPE, SIDE } from "@/lib/format";
import { HelpTip } from "@/components/kit";
import { LogoMark } from "@/components/Logo";

const PRESETS = { forex: [1000, 10000, 50000], crypto: [0.01, 0.1, 1], stocks: [1, 5, 10], indices: [0.1, 0.5, 1], commodities: [1, 10, 50] };

const Row = ({ k, v, testid, cls = "" }) => (
  <div className="flex justify-between text-sm"><span className="text-muted-foreground">{k}</span><span className={`num ${cls}`} data-testid={testid}>{v}</span></div>
);

export const OrderTicket = ({ q, onPlaced }) => {
  const [side, setSide] = useState("buy");
  const [type, setType] = useState("market");
  const [qty, setQty] = useState(PRESETS[q.category][0]);
  const [price, setPrice] = useState("");
  const [useSl, setUseSl] = useState(false);
  const [useTp, setUseTp] = useState(false);
  const [sl, setSl] = useState("");
  const [tp, setTp] = useState("");
  const [pv, setPv] = useState(null);
  const [confirm, setConfirm] = useState(false);
  const [busy, setBusy] = useState(false);

  const body = () => ({ symbol: q.symbol, side, order_type: type, quantity: Number(qty), price: type === "market" ? null : Number(price) || null,
    stop_loss: useSl && sl ? Number(sl) : null, take_profit: useTp && tp ? Number(tp) : null });

  const ref = side === "buy" ? q.ask : q.bid;
  const refRounded = Math.round((ref || 0) * 1e6) / 1e6;
  useEffect(() => {
    if (!qty || Number(qty) <= 0) { setPv(null); return; }
    const id = setTimeout(() => api.post("/trading/preview", body()).then((r) => setPv(r.data)).catch(() => setPv(null)), 350);
    return () => clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [side, type, qty, price, sl, tp, useSl, useTp, q.symbol, refRounded]);

  const suggest = (dir) => {
    const p = type === "market" ? ref : Number(price) || ref;
    const off = p * (q.category === "forex" ? 0.003 : 0.02);
    return (p + dir * off).toFixed(q.decimals);
  };

  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/trading/orders", { ...body(), confirm: true });
      toast.success(data.order.status === "filled" ? `Ordem executada a ${fmtPrice(data.order.fill_price, q.decimals)}` : "Ordem pendente registrada");
      onPlaced?.();
      window.dispatchEvent(new Event("tr:notifications"));
    } catch (e) { toast.error(errMsg(e)); onPlaced?.(); }
    finally { setBusy(false); setConfirm(false); }
  };

  const disabled = !q.trading_enabled || q.price == null || !qty || (type !== "market" && !price);
  return (
    <div className="surface p-5 md:p-6" data-testid="order-ticket">
      <div className="flex items-center justify-between">
        <div className="eyebrow">Boleta de negociação</div>
        <span className="rounded-full bg-violet-400/15 px-2 py-0.5 text-[10px] font-bold text-violet-400" data-testid="ticket-demo-badge">DEMO</span>
      </div>
      <div className="mt-4 grid grid-cols-2 gap-2">
        <Button variant="outline" onClick={() => setSide("sell")} data-testid="ticket-side-sell"
          className={`h-14 flex-col rounded-xl ${side === "sell" ? "border-down bg-down-soft text-down" : ""}`}>
          <span className="text-xs">Vender</span><span className="num font-semibold">{fmtPrice(q.bid, q.decimals)}</span></Button>
        <Button variant="outline" onClick={() => setSide("buy")} data-testid="ticket-side-buy"
          className={`h-14 flex-col rounded-xl ${side === "buy" ? "border-up bg-up-soft text-up" : ""}`}>
          <span className="text-xs">Comprar</span><span className="num font-semibold">{fmtPrice(q.ask, q.decimals)}</span></Button>
      </div>
      <Tabs value={type} onValueChange={setType} className="mt-4">
        <TabsList className="grid grid-cols-3 w-full">{Object.entries(ORDER_TYPE).map(([k, l]) => <TabsTrigger key={k} value={k} data-testid={`ticket-type-${k}`}>{l}</TabsTrigger>)}</TabsList>
      </Tabs>
      <p className="mt-2 text-xs text-muted-foreground">
        {type === "market" && "Executa imediatamente ao melhor preço disponível."}
        {type === "limit" && "Executa somente quando o preço atingir o valor definido ou melhor."}
        {type === "stop" && "Dispara quando o preço romper o valor definido (rompimento)."}
      </p>
      <div className="mt-4 space-y-4">
        <div className="space-y-2">
          <Label className="flex items-center gap-1.5">Quantidade<HelpTip text="Número de unidades do ativo. Em Forex, 1.000 unidades = 0,01 lote." /></Label>
          <Input type="number" min="0" step="any" className="h-11 num" value={qty} onChange={(e) => setQty(e.target.value)} data-testid="ticket-quantity-input" />
          <div className="flex gap-2">{PRESETS[q.category].map((p) => <Button key={p} size="sm" variant="secondary" onClick={() => setQty(p)} data-testid={`ticket-qty-preset-${p}`}>{p}</Button>)}</div>
        </div>
        {type !== "market" && (
          <div className="space-y-2"><Label>Preço da ordem</Label>
            <Input type="number" step="any" className="h-11 num" value={price} placeholder={fmtPrice(ref, q.decimals)} onChange={(e) => setPrice(e.target.value)} data-testid="ticket-price-input" /></div>
        )}
        {[["Stop Loss", useSl, setUseSl, sl, setSl, side === "buy" ? -1 : 1, "sl", "Fecha a posição automaticamente para limitar o prejuízo."],
          ["Take Profit", useTp, setUseTp, tp, setTp, side === "buy" ? 1 : -1, "tp", "Fecha a posição automaticamente ao atingir o lucro desejado."]].map(([label, on, setOn, val, setVal, dir, id, help]) => (
          <div key={id} className="space-y-2">
            <div className="flex items-center justify-between"><Label className="flex items-center gap-1.5">{label}<HelpTip text={help} /></Label>
              <Switch checked={on} onCheckedChange={(v) => { setOn(v); if (v && !val) setVal(suggest(dir)); }} data-testid={`ticket-${id}-switch`} /></div>
            {on && <Input type="number" step="any" className="h-11 num" value={val} onChange={(e) => setVal(e.target.value)} data-testid={`ticket-${id}-input`} />}
          </div>
        ))}
      </div>
      <div className="mt-5 space-y-2 rounded-xl bg-muted/50 p-4" data-testid="ticket-estimate">
        <Row k="Preço de referência" v={fmtPrice(pv?.reference_price, q.decimals)} />
        <Row k="Valor nocional" v={fmtMoney(pv?.notional)} testid="ticket-notional" />
        <Row k={`Margem (alavancagem 1:${q.leverage})`} v={fmtMoney(pv?.margin)} testid="ticket-margin" />
        <Row k="Comissão estimada" v={fmtMoney(pv?.commission)} testid="ticket-commission" />
        <Row k="Saldo disponível" v={fmtMoney(pv?.available)} cls={pv && !pv.sufficient ? "down" : ""} testid="ticket-available" />
      </div>
      {pv?.level_error && <p className="mt-3 flex gap-2 text-xs down" data-testid="ticket-level-error"><AlertTriangle className="h-4 w-4 shrink-0" />{pv.level_error}</p>}
      {pv && !pv.sufficient && <p className="mt-3 text-xs down" data-testid="ticket-insufficient">Saldo disponível insuficiente para esta operação.</p>}
      {!q.trading_enabled && <p className="mt-3 text-xs down">Negociação desabilitada para este ativo pelo administrador.</p>}
      <Button disabled={disabled} onClick={() => setConfirm(true)} data-testid="ticket-review-button"
        className={`mt-5 h-12 w-full rounded-xl text-base font-semibold ${side === "buy" ? "bg-up hover:bg-up/90" : "bg-down hover:bg-down/90"} text-white`}>
        Revisar {side === "buy" ? "compra" : "venda"}
      </Button>
      <AlertDialog open={confirm} onOpenChange={setConfirm}>
        <AlertDialogContent data-testid="order-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-3"><LogoMark size={36} className="rounded-lg" />Confirmar ordem (modo demonstração)</AlertDialogTitle>
            <AlertDialogDescription>Revise os dados. A ordem será simulada com saldo virtual; nenhuma ordem real é enviada.</AlertDialogDescription>
          </AlertDialogHeader>
          <div className="space-y-2 rounded-xl border border-border p-4">
            <Row k="Ativo" v={`${q.name} (${q.symbol})`} />
            <Row k="Operação" v={`${SIDE[side]} · ${ORDER_TYPE[type]}`} cls={side === "buy" ? "up" : "down"} />
            <Row k="Quantidade" v={qty} />
            <Row k="Preço" v={fmtPrice(pv?.reference_price, q.decimals)} />
            {useSl && sl && <Row k="Stop Loss" v={sl} />}
            {useTp && tp && <Row k="Take Profit" v={tp} />}
            <Row k="Margem" v={fmtMoney(pv?.margin)} />
            <Row k="Comissão" v={fmtMoney(pv?.commission)} />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="order-confirm-cancel">Cancelar</AlertDialogCancel>
            <AlertDialogAction disabled={busy} onClick={submit} data-testid="order-confirm-submit">{busy ? "Enviando…" : "Confirmar ordem"}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
